import React, { useEffect, useState } from 'react';
import { UncontrolledTooltip, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { getCommsInfo, retriggerComms } from '../../../services/api/cashless-dentals';
import LoadingComponent from '../../common/LoadingComponent';
import Retrigger from '../../../images/Retrigger.svg';
import Content from '../../../images/Content.svg';
import Whatsapp from '../../../images/Whatsapp.svg';
import Comment from '../../../images/Comment.svg';
import Mail from '../../../images/Mail.svg';
import Email from '../../../images/Email.svg';
import Comments from '../../../images/Comments.svg';

const SERVICE_KEY = 'cashless_dental';

// Keeps CTA buttons/links (e.g. "View Dashboard", "Submit here") visible in the
// previewed email, but removes their navigation so nothing is clickable in the
// ops dashboard.
const disableEmailLinks = (html) => {
  if (!html || typeof html !== 'string') return html;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('a, button').forEach((el) => {
      el.removeAttribute('href');
      el.removeAttribute('target');
      el.removeAttribute('onclick');
      if (el.style) el.style.cursor = 'default';
    });
    return doc.documentElement ? doc.documentElement.outerHTML : html;
  } catch (err) {
    console.error('[CommsLog] disableEmailLinks', err);
    return html;
  }
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const LogsTable = styled.table`
  width: 100%;
`;

const ChannelIcon = styled.img`
  height: 24px;
  width: 44px;
`;

const ActionIcon = styled.img`
  cursor: pointer;
  margin-right: ${(props) => (props.spaced ? '8px' : '0')};
`;

// receivers are emails/phone numbers that can be long enough to widen the table
const ReceiverCell = styled.td`
  word-break: break-word;
  max-width: 220px;
`;

const EmptyState = styled.div`
  color: #999;
  font-size: 1rem;
  padding: 1rem 0;
`;

const ContentPanel = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #f2f1f6;
  border-radius: 12px;
  overflow: hidden;
  width: 100%;
  margin-top: 12px;
`;

const ContentRow = styled.div`
  display: flex;
  border-bottom: ${(props) => (props.last ? 'none' : '1px solid #f2f1f6')};
  background-color: ${(props) => (props.header ? '#cfc8ff' : 'transparent')};
`;

const PlatformCell = styled.div`
  flex: 0 0 30%;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  font-family: 'Inter';
  font-weight: ${(props) => (props.bold ? '700' : '400')};

  span {
    margin-left: 8px;
    font-family: 'Inter';
    font-weight: 700;
  }
`;

const ContentCell = styled.div`
  flex: 1;
  padding: 8px 12px;
  font-family: 'Inter';
  font-weight: ${(props) => (props.bold ? '700' : '400')};
`;

const retriggerTooltipStyle = { backgroundColor: '#FFFFFF', color: '#3F8CFF', border: '1px solid #3F8CFF' };
const contentTooltipStyle = { backgroundColor: '#FFFFFF', color: '#FF865C', border: '1px solid #FF865C' };

const CommsLog = ({ requestId }) => {
  const [logsData, setLogsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [commsContentId, setCommsContentId] = useState(null);
  const [retriggerOpen, setRetriggerOpen] = useState(false);
  const [eventType, setEventType] = useState(null);

  const fetchLogs = () => {
    if (!requestId) return;
    setLoading(true);
    Promise.resolve()
      .then(() => getCommsInfo(requestId, SERVICE_KEY))
      .then((response) => {
        const data = Array.isArray(response?.getCommsData) ? response.getCommsData : [];
        // keep only valid object entries so malformed rows can't break grouping/render
        setLogsData(data.filter((item) => item && typeof item === 'object'));
      })
      .catch((err) => {
        console.error('[CommsLog] fetchLogs', err);
        setLogsData([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const toggleContent = (id) => {
    setCommsContentId((prev) => (prev === id ? null : id));
  };

  const toggleRetriggerModal = (type) => {
    setEventType(type || null);
    setRetriggerOpen((prev) => !prev);
  };

  const handleRetrigger = async () => {
    try {
      const res = await retriggerComms(requestId, eventType, SERVICE_KEY);
      if (res?.message === 'success' || res?.success) {
        toast.success('Retriggered Successfully');
      } else {
        toast.error('Something went wrong');
      }
    } catch (err) {
      console.error('[CommsLog] retrigger', err);
      toast.error('Something went wrong');
    } finally {
      setRetriggerOpen(false);
    }
  };

  if (loading) {
    return <LoadingComponent />;
  }

  if (!logsData.length) {
    return <EmptyState>No communications found.</EmptyState>;
  }

  const groupedData = logsData.reduce((groups, item) => {
    if (!item) return groups;
    const key = item.eventType || 'Unknown';
    const group = groups[key] || [];
    group.push(item);
    groups[key] = group;
    return groups;
  }, {});

  const selectedLog = logsData.find((log) => log && log.commsContentId === commsContentId);

  return (
    <Wrapper>
      <TableScroll>
        <LogsTable className="table-container">
          <thead>
            <tr>
              <th>Event</th>
              <th>User Type</th>
              <th>Triggered</th>
              <th>Channel</th>
              <th>Receiver</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(groupedData).map(([evtType, items]) => (
              <React.Fragment key={evtType}>
                {items.map((item, index) => (
                  <tr key={item.id ?? `${evtType}-${index}`}>
                    {index === 0 && <td rowSpan={items.length}>{evtType}</td>}
                    <td>{item.userType}</td>
                    <td>{item.triggeredAt}</td>
                    <td>
                      {item.emailContent && <ChannelIcon src={Email} alt="email" />}
                      {item.whatsappContent && <ChannelIcon src={Whatsapp} alt="whatsapp" />}
                      {item.smsContent && <ChannelIcon src={Comments} alt="sms" />}
                    </td>
                    <ReceiverCell>{item.receiver || '-'}</ReceiverCell>
                    <td>
                      <ActionIcon
                        spaced
                        id={`retrigger-${item.commsContentId}`}
                        src={Retrigger}
                        alt="Retrigger"
                        onClick={() => toggleRetriggerModal(item.eventType)}
                      />
                      <UncontrolledTooltip style={retriggerTooltipStyle} target={`retrigger-${item.commsContentId}`}>
                        Retrigger
                      </UncontrolledTooltip>
                      <ActionIcon
                        id={`content-${item.commsContentId}`}
                        src={Content}
                        alt="Content"
                        onClick={() => toggleContent(item.commsContentId)}
                      />
                      <UncontrolledTooltip style={contentTooltipStyle} target={`content-${item.commsContentId}`}>
                        Content
                      </UncontrolledTooltip>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </LogsTable>
      </TableScroll>

      {selectedLog && (
        <ContentPanel>
          <ContentRow header>
            <PlatformCell bold>Platform</PlatformCell>
            <ContentCell bold>Content</ContentCell>
          </ContentRow>
          <ContentRow>
            <PlatformCell>
              <img src={Whatsapp} alt="whatsapp" />
              <span>Whatsapp</span>
            </PlatformCell>
            <ContentCell>{selectedLog.whatsappContent || 'NA'}</ContentCell>
          </ContentRow>
          <ContentRow>
            <PlatformCell>
              <img src={Comment} alt="sms" />
              <span>SMS</span>
            </PlatformCell>
            <ContentCell>{selectedLog.smsContent || 'NA'}</ContentCell>
          </ContentRow>
          <ContentRow last>
            <PlatformCell>
              <img src={Mail} alt="email" />
              <span>Email</span>
            </PlatformCell>
            <ContentCell>
              {selectedLog.emailContent ? (
                <div dangerouslySetInnerHTML={{ __html: disableEmailLinks(selectedLog.emailContent) }} />
              ) : (
                'NA'
              )}
            </ContentCell>
          </ContentRow>
        </ContentPanel>
      )}

      <Modal isOpen={retriggerOpen} toggle={() => toggleRetriggerModal()}>
        <ModalHeader toggle={() => toggleRetriggerModal()}>Retrigger Communication</ModalHeader>
        <ModalBody>Are you sure you want to retrigger the communication?</ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleRetrigger}>
            Retrigger
          </Button>
          <Button onClick={() => toggleRetriggerModal()}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </Wrapper>
  );
};

export default CommsLog;
